import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Field, Form, Formik, FormikProps } from 'formik';

import * as Styled from '../LoyaltyPageSeparated.styles';
import { selectors } from '../../LoyaltyPage/reducer';
import * as actions from '../../LoyaltyPage/actions';
import { useBarcodeScanner } from '../../LoyaltyPage/hooks/useBarcodeScanner';
import qrScan from '../../../global/media/qr-scan.png';
import { ScreenType } from './LoyaltyTabs';
import {
  CurrentReceiptData,
  GetClientOrdersData,
} from '../../LoyaltyPage/types';
import { isCouponEntry } from '../../LoyaltyPageV3/coupons/model';
import { couponErrorKey } from '../../LoyaltyPageV3/coupons/service';

interface LoyaltyClientScreenProps {
  onCouponErrorChange?: (error: string) => void;
  onClientAdded: () => void;
  isStadaBonusesCredit: boolean;
  isPartnersPayment: boolean;
  onScreenChange: (screen: ScreenType) => void;
  onCouponEntered?: (code: string) => Promise<void>;
  onPendingOrderState?: (order: GetClientOrdersData | null) => void;
  onPendingOrderRestored?: () => void;
}

const LoyaltyClientScreen: React.FC<LoyaltyClientScreenProps> = ({
  onClientAdded,
  onCouponErrorChange,
  isStadaBonusesCredit,
  isPartnersPayment,
  onScreenChange,
  onCouponEntered,
  onPendingOrderState,
  onPendingOrderRestored,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const clientInfo = useSelector(selectors.clientInfo);
  const stadaInfo = useSelector(selectors.stadaInfo);
  const clientInfoLoading = useSelector(selectors.clientInfoLoading);
  const clientInfoError = useSelector(selectors.clientInfoError);

  const [clientPhone, setClientPhone] = useState<string>('+77');
  const [phoneError, setPhoneError] = useState<boolean>(false);
  const [scanMode, setScanMode] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  useEffect(() => {
    onCouponErrorChange?.(couponError);
  }, [couponError, onCouponErrorChange]);
  useEffect(() => () => onCouponErrorChange?.(''), [onCouponErrorChange]);

  const formikRef = useRef<FormikProps<any> | null>(null);
  const prevClientRef = useRef<boolean>(false);
  const autoTransitionRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stadaBalance = (stadaInfo && stadaInfo.balance) ?? 0;

  const getClientInfoError = useMemo(() => {
    switch (clientInfoError) {
      case undefined:
        return null;
      case 'loyalty code not found':
        return t('LoyaltySeparated.CODE_NOT_FOUND');
      default:
        return t('LoyaltySeparated.ERROR');
    }
  }, [clientInfoError, t]);

  const onScan = useCallback((text: string) => {
    const m = text.match(/[?&](phone|tel)=([^&]+)/i);
    const raw = m ? decodeURIComponent(m[2]) : text;
    if (raw.match(/^\+77\d{0,9}$/)) {
      setClientPhone(raw);
      setPhoneError(false);
    } else {
      setPhoneError(true);
    }
    setScanMode(false);
  }, []);

  useBarcodeScanner({
    enabled: scanMode,
    onScan,
    endKeys: ['Enter', 'Tab', 'NumpadEnter'],
  });

  const getClientBalance = useCallback(
    ({ phone, code }: { phone?: string; code?: string }) => {
      const postEffect = isPartnersPayment
        ? {
            postEffect: (clientPhone: string) => {
              dispatch(
                actions.getClientOrders.request({
                  phone: clientPhone,
                  postEffect: (order: GetClientOrdersData) => {
                    if (!order) {
                      onPendingOrderState?.(null);
                      autoTransitionRef.current = setTimeout(() => {
                        autoTransitionRef.current = null;
                        setShowSuccess(false);
                        onClientAdded();
                      }, 1500);
                      return;
                    }

                    dispatch(
                      actions.getCurrentReceipt.request({
                        postEffect: (res: CurrentReceiptData[]) => {
                          const matchingReceipt = res?.find(
                            (item: CurrentReceiptData) =>
                              item.doc_id === order.doc_id
                          );

                          if (!matchingReceipt) {
                            dispatch(actions.setChosenReceipt.request(null));
                            dispatch(actions.resetSubmitOrder());
                            setShowSuccess(false);
                            onPendingOrderState?.(order);
                            onClientAdded();
                            return;
                          }

                          dispatch(
                            actions.setChosenReceipt.request(matchingReceipt)
                          );
                          onPendingOrderState?.(null);
                          setShowSuccess(false);
                          if (onPendingOrderRestored) {
                            onPendingOrderRestored();
                          } else {
                            onScreenChange('payment');
                          }
                        },
                      })
                    );
                  },
                })
              );
            },
          }
        : {};

      dispatch(actions.getClientInfo.request({ phone, code, ...postEffect }));
    },
    [
      dispatch,
      isPartnersPayment,
      onClientAdded,
      onPendingOrderRestored,
      onPendingOrderState,
      onScreenChange,
    ]
  );

  const submitCode = useCallback(
    async (code: string) => {
      if (!code) return;
      if (couponLoading || clientInfoLoading) return;
      setCouponError('');
      if (onCouponEntered && /[A-Za-z]/.test(code)) {
        if (!isCouponEntry(code)) {
          setCouponError('INVALID_CODE');
          return;
        }
        setCouponLoading(true);
        try {
          await onCouponEntered(code);
        } catch (error) {
          setCouponError(couponErrorKey(error));
        } finally {
          setCouponLoading(false);
        }
        return;
      }
      const digitsOnlyCode = code.replace(/\D/g, '');
      if (digitsOnlyCode.length >= 6) {
        dispatch(actions.resetClientInfo());
        getClientBalance({ code: digitsOnlyCode });
      } else {
        dispatch(actions.resetClientInfo());
      }
    },
    [
      dispatch,
      couponLoading,
      clientInfoLoading,
      getClientBalance,
      onCouponEntered,
    ]
  );

  const handleCodeChange = (
    props: FormikProps<any>,
    event: ChangeEvent<HTMLInputElement>,
    inputName: string
  ) => {
    // @ts-ignore
    const codeInputValue = event.target.value.replaceAll(/[\s-()]/g, '');
    if (
      !codeInputValue.match(
        onCouponEntered ? /^[A-Za-z0-9_]{0,11}$/ : /^\d{0,11}$/
      )
    )
      return;
    setCouponError('');
    props.setErrors({ ...props.errors, [inputName]: '' });
    props.setFieldValue(inputName, codeInputValue);

    // Очищаем текущего клиента при начале ввода нового кода
    if (clientInfo) {
      dispatch(actions.resetClientInfo());
    }
  };

  // Поиск по телефону (qr scan)
  useEffect(() => {
    if (!clientPhone || clientPhone === '+77') return;
    setPhoneError(false);
    const digitsOnly = clientPhone.replace(/\D/g, '');
    if (digitsOnly.length >= 11) {
      getClientBalance({ phone: digitsOnly });
    } else {
      dispatch(actions.resetClientInfo());
      formikRef.current?.resetForm({ values: { code: '' } });
    }
  }, [clientPhone, dispatch, getClientBalance]);

  // Авто-переход: только при переходе из «нет клиента» → «клиент найден»
  useEffect(() => {
    const hadClientBefore = prevClientRef.current;
    const hasClientNow = !!clientInfo && !clientInfoLoading && !clientInfoError;

    // Переход сработает только когда клиент ТОЛЬКО ЧТО появился
    if (hasClientNow && !hadClientBefore) {
      setShowSuccess(true);
      // и если нет логики оплаты в приложении
      if (!isPartnersPayment) {
        autoTransitionRef.current = setTimeout(() => {
          autoTransitionRef.current = null;
          setShowSuccess(false);
          onClientAdded();
        }, 1500);
      }
    }

    // Если клиент пропал (reset), убираем toast
    if (!clientInfo && hadClientBefore) {
      setShowSuccess(false);
    }

    prevClientRef.current = !!clientInfo;

    return () => {
      if (autoTransitionRef.current) {
        clearTimeout(autoTransitionRef.current);
      }
    };
  }, [
    clientInfo,
    clientInfoLoading,
    clientInfoError,
    isPartnersPayment,
    onClientAdded,
  ]);

  // Ресет при сканировании
  useEffect(() => {
    if (scanMode) {
      setClientPhone('+77');
      setPhoneError(false);
      setCouponError('');
      dispatch(actions.resetClientInfo());
      formikRef.current?.resetForm({ values: { code: '' } });
    }
  }, [dispatch, scanMode]);

  return (
    <Styled.ClientBlock>
      <Styled.BlockTitle>
        {t('LoyaltySeparated.CLIENT_SECTION')}
      </Styled.BlockTitle>
      <Styled.BlockHint>
        {t(
          onCouponEntered
            ? 'LoyaltyCoupons.ENTRY_HINT'
            : 'LoyaltySeparated.CLIENT_ADD_HINT'
        )}
      </Styled.BlockHint>

      <Styled.ClientActions>
        {/* QR Scan */}
        {process.env.REACT_APP_LOYALTY_BARCODE_ACTIVE === 'true' && (
          <Styled.ScanButton
            disabled={couponLoading || clientInfoLoading}
            onClick={() => setScanMode((m) => !m)}
          >
            {scanMode ? (
              <CircularProgress
                size={18}
                color="inherit"
                sx={{ margin: '2px 8px' }}
              />
            ) : (
              <>
                <img width={20} height={20} src={qrScan} alt="qr scan icon" />
                {t('LoyaltySeparated.SCAN_QR')}
              </>
            )}
          </Styled.ScanButton>
        )}

        {/* Поле ввода кода */}
        {!scanMode && (
          <>
            {process.env.REACT_APP_LOYALTY_BARCODE_ACTIVE === 'true' && (
              <Styled.OrDivider>{t('LoyaltySeparated.OR')}</Styled.OrDivider>
            )}

            <Styled.CodeForm>
              <Formik
                innerRef={(ref) => {
                  formikRef.current = ref;
                }}
                initialValues={{ code: '' }}
                enableReinitialize
                onSubmit={() => {}}
              >
                {(props: FormikProps<any>) => (
                  <Form className="form-container">
                    <div className="form-block">
                      <Field
                        id="code"
                        name="code"
                        type={onCouponEntered ? 'text' : 'number'}
                        inputMode={onCouponEntered ? 'text' : 'numeric'}
                        autoComplete="off"
                        disabled={couponLoading || clientInfoLoading}
                        placeholder={t(
                          onCouponEntered
                            ? 'LoyaltyCoupons.ENTRY_PLACEHOLDER'
                            : 'LoyaltySeparated.ENTER_CODE'
                        )}
                        className={`input ${
                          props.errors.code && 'input-error'
                        }`}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          handleCodeChange(props, e, 'code')
                        }
                      />
                      {phoneError && (
                        <div className="error-phone-label">
                          {t('LoginPage.PHONE_INCORRECT_PREFIX')}
                        </div>
                      )}
                    </div>

                    <Styled.ApplyCodeButton
                      type="button"
                      disabled={couponLoading || clientInfoLoading}
                      onClick={() => submitCode(props.values.code as string)}
                    >
                      {t('LoyaltySeparated.APPLY')}
                    </Styled.ApplyCodeButton>
                  </Form>
                )}
              </Formik>
            </Styled.CodeForm>
          </>
        )}

        {/* Лоадер */}
        {(clientInfoLoading || couponLoading) && <CircularProgress size={18} />}
        {couponError && (
          <Styled.ErrorLabel role="alert">
            {t(`LoyaltyCoupons.Errors.${couponError}`)}
          </Styled.ErrorLabel>
        )}

        {/* Ошибка */}
        {clientInfoError && (
          <Styled.ErrorLabel>{getClientInfoError}</Styled.ErrorLabel>
        )}

        {/* Баланс клиента */}
        {!clientInfoLoading && clientInfo && (
          <Styled.BalanceBadge>
            {'balance' in clientInfo ? clientInfo.balance : ''}{' '}
            {t('LoyaltySeparated.BONUS_SHORT')}
          </Styled.BalanceBadge>
        )}

        {/* Штадовский баланс клиента — показываем рядом если есть */}
        {!clientInfoLoading &&
          clientInfo &&
          stadaInfo &&
          isStadaBonusesCredit &&
          stadaBalance > 0 && (
            <Styled.BalanceBadge>
              STADA: {stadaBalance} {t('LoyaltySeparated.BONUS_SHORT')}
            </Styled.BalanceBadge>
          )}
      </Styled.ClientActions>

      {/* Статус наличия данных клиента */}
      {!clientInfoLoading && (
        <Styled.ClientStatus $added={!!clientInfo}>
          <img
            src={`/images/client${clientInfo ? '' : '_not'}_added.png`}
            width={20}
            height={20}
            alt="icon"
          />
          {t(`LoyaltyPage.CLIENT${clientInfo ? '' : '_NOT'}_ADDED`)}
        </Styled.ClientStatus>
      )}

      {/* Подтверждение успешного добавления клиента */}
      {showSuccess && (
        <Styled.SuccessToast>
          ✓ {t('LoyaltySeparated.CLIENT_SUCCESS')}
        </Styled.SuccessToast>
      )}
    </Styled.ClientBlock>
  );
};

export default LoyaltyClientScreen;
